import { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { subscriptionsApi, type UserSubscription } from '@/api/subscriptions'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  Divider,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  CheckCircle,
  Cancel,
  Schedule,
  Autorenew,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { ru, enUS } from 'date-fns/locale'
import { ROUTES } from '@/utils/constants'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { userSettingsApi } from '@/api/userSettings'
import { getErrorMessage } from '@/utils/error'

function getSubscriptionStatusLabel(
  subscription: UserSubscription,
  t: (key: string) => string
): { label: string; color: 'success' | 'default' | 'warning' | 'error' } {
  if (subscription.payment?.status === 'pending') {
    return { label: t('subscription.status.pending_payment'), color: 'warning' }
  }
  const expiresAt = subscription.expires_at ? new Date(subscription.expires_at) : null
  const now = new Date()
  if (expiresAt && expiresAt < now) {
    return { label: t('subscription.status.expired'), color: 'default' }
  }
  if (subscription.is_active && subscription.is_cancelled) {
    return { label: t('subscription.status.cancelledActive'), color: 'warning' }
  }
  if (subscription.is_active) {
    return { label: t('subscription.status.active'), color: 'success' }
  }
  return { label: t('subscription.status.cancelled'), color: 'default' }
}

export function SubscriptionPage() {
  const { t, i18n } = useTranslation()
  const user = useAuthStore((state) => state.user)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [subscriptionsList, setSubscriptionsList] = useState<UserSubscription[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubscribing, setIsSubscribing] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [subscriptionToCancel, setSubscriptionToCancel] = useState<UserSubscription | null>(null)
  const [autoRenewEnabled, setAutoRenewEnabled] = useState(true)
  const [autoRenewSaving, setAutoRenewSaving] = useState(false)
  const hasLoadedRef = useRef(false)

  const canManageSubscription = user?.roles?.some((r) => r.slug === 'teacher')
  const dateLocale = i18n.language === 'ru' ? ru : enUS

  const currentSubscription =
    subscriptionsList.find((s) => s.is_active) ??
    subscriptionsList.find((s) => s.payment?.status === 'pending') ??
    null

  useEffect(() => {
    if (!canManageSubscription) return
    if (hasLoadedRef.current) return
    hasLoadedRef.current = true
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageSubscription])

  const loadData = async () => {
    if (!canManageSubscription) return
    try {
      setIsLoading(true)
      setError(null)
      const [list, settings] = await Promise.all([
        subscriptionsApi.getSubscriptionsList(),
        userSettingsApi.getMySettings().catch(() => null),
      ])
      setSubscriptionsList(Array.isArray(list) ? list : [])
      // null в настройке = значение по умолчанию (автопродление включено)
      setAutoRenewEnabled(settings?.subscription_auto_renew ?? true)
    } catch (err: unknown) {
      setError(getErrorMessage(err) || t('subscription.loadError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleAutoRenewToggle = async (enabled: boolean) => {
    setAutoRenewEnabled(enabled)
    setAutoRenewSaving(true)
    setError(null)
    try {
      await userSettingsApi.updateMySettings({ subscription_auto_renew: enabled })
    } catch (err: unknown) {
      setAutoRenewEnabled(!enabled)
      setError(getErrorMessage(err) || t('subscription.autoRenewSaveError', 'Не удалось сохранить настройку автопродления'))
    } finally {
      setAutoRenewSaving(false)
    }
  }

  const handleCancelClick = (sub: UserSubscription) => {
    setSubscriptionToCancel(sub)
    setCancelDialogOpen(true)
  }

  const handleCancelConfirm = async () => {
    if (!subscriptionToCancel) return
    setCancelDialogOpen(false)
    try {
      setIsSubscribing(true)
      setError(null)
      await subscriptionsApi.deactivateSubscription(subscriptionToCancel.id)
      await loadData()
    } catch (err: unknown) {
      setError(getErrorMessage(err) || t('subscription.cancelError'))
    } finally {
      setIsSubscribing(false)
      setSubscriptionToCancel(null)
    }
  }

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false)
    setSubscriptionToCancel(null)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: dateLocale })
    } catch {
      return dateString
    }
  }

  const canShowCancelFor = (sub: UserSubscription) =>
    sub.is_active && !sub.is_cancelled && sub.payment?.status !== 'pending'

  if (!canManageSubscription) {
    return (
      <ProfileLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="info">{t('subscription.studentInfo')}</Alert>
        </Box>
      </ProfileLayout>
    )
  }

  return (
    <ProfileLayout>
      <Box sx={{ p: 3, pt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('subscription.title')}
          </Typography>
          <PageHelpPopover pageKey="profile-subscription" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Current subscription */}
        {isLoading ? (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="50%" height={32} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="40%" height={28} />
                </Box>
                <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" height={24} />
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" height={24} />
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" height={24} />
                </Box>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Skeleton variant="rectangular" width={200} height={36} sx={{ borderRadius: 1 }} />
              </Box>
            </CardContent>
          </Card>
        ) : currentSubscription?.plan ? (
          <Card sx={{ mb: 4, border: '2px solid', borderColor: 'primary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Box>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {t('subscription.currentSubscription')}
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {currentSubscription.plan.name}
                  </Typography>
                </Box>
                {(() => {
                  const status = getSubscriptionStatusLabel(currentSubscription, t)
                  const isExpired =
                    currentSubscription.expires_at && new Date(currentSubscription.expires_at) < new Date()
                  const statusIcon = isExpired ? (
                    <Cancel />
                  ) : currentSubscription.payment?.status === 'pending' ? (
                    <Schedule />
                  ) : currentSubscription.is_cancelled ? (
                    <Schedule />
                  ) : currentSubscription.is_active ? (
                    <CheckCircle />
                  ) : (
                    <Cancel />
                  )
                  return (
                    <Chip
                      label={status.label}
                      color={status.color}
                      icon={statusIcon}
                    />
                  )
                })()}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Schedule fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('subscription.startedAt')}
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {formatDate(currentSubscription.starts_at)}
                  </Typography>
                  {currentSubscription.starts_at && new Date(currentSubscription.starts_at) > new Date() && (() => {
                    const diffDays = Math.ceil((new Date(currentSubscription.starts_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                    return (
                      <Typography variant="body2" color="info.main" sx={{ mt: 0.5 }}>
                        {t('subscription.startsInDays', { days: diffDays })}
                      </Typography>
                    )
                  })()}
                </Box>

                {currentSubscription.expires_at && (
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Schedule fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {t('subscription.expiresAt')}
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {formatDate(currentSubscription.expires_at)}
                    </Typography>
                    {currentSubscription.remaining_days !== undefined &&
                      !(currentSubscription.starts_at && new Date(currentSubscription.starts_at) > new Date()) && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t('subscription.remainingDays', { days: currentSubscription.remaining_days })}
                      </Typography>
                    )}
                  </Box>
                )}

                {currentSubscription.plan.auto_renewal !== undefined && (
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Autorenew fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {t('subscription.autoRenew')}
                      </Typography>
                    </Box>
                    {currentSubscription.is_cancelled || !currentSubscription.plan.auto_renewal ? (
                      <Typography variant="body1">{t('common.no')}</Typography>
                    ) : (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={autoRenewEnabled}
                              onChange={(_, checked) => handleAutoRenewToggle(checked)}
                              disabled={autoRenewSaving}
                              size="small"
                            />
                          }
                          label={
                            autoRenewEnabled
                              ? t('subscription.autoRenewToggleOn', 'Включено')
                              : t('subscription.autoRenewToggleOff', 'Выключено')
                          }
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {t(
                            'subscription.autoRenewBalanceHint',
                            'Продление автоматически списывается с вашего баланса.',
                          )}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
              </Box>

              {currentSubscription.is_cancelled &&
                currentSubscription.expires_at &&
                new Date(currentSubscription.expires_at) >= new Date() && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('subscription.cancelledInfo', { date: formatDate(currentSubscription.expires_at) })}
                  </Alert>
                )}

              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  component={Link}
                  to={ROUTES.PROFILE_PLANS}
                >
                  {t('subscription.changePlan')}
                </Button>
                {canShowCancelFor(currentSubscription) && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleCancelClick(currentSubscription)}
                    disabled={isSubscribing}
                  >
                    {t('subscription.cancel')}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                {t('subscription.currentSubscription')}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>{t('subscription.noCurrentSubscription')}</Typography>
              <Button
                variant="contained"
                component={Link}
                to={ROUTES.PROFILE_PLANS}
              >
                {t('subscription.choosePlan')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Subscription history */}
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, mt: 4 }}>
          {t('subscription.history')}
        </Typography>
        {isLoading ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {subscriptionsList.length === 0 ? (
              <Typography color="text.secondary">{t('subscription.noCurrentSubscription')}</Typography>
            ) : (
              subscriptionsList.map((sub) => (
                <Card key={sub.id}>
                  <CardContent>
                    <Typography variant="subtitle1">{sub.plan?.name ?? '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(sub.starts_at)} — {formatDate(sub.expires_at)}
                    </Typography>
                    <Chip
                      size="small"
                      label={getSubscriptionStatusLabel(sub, t).label}
                      color={getSubscriptionStatusLabel(sub, t).color}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('subscription.currentPlan')}</TableCell>
                  <TableCell>{t('subscription.startedAt')}</TableCell>
                  <TableCell>{t('subscription.expiresAt')}</TableCell>
                  <TableCell>{t('subscription.paymentMethodColumn', 'Оплата')}</TableCell>
                  <TableCell align="right">{t('subscription.statusColumn')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptionsList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      {t('subscription.noCurrentSubscription')}
                    </TableCell>
                  </TableRow>
                ) : (
                  subscriptionsList.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell>{sub.plan?.name ?? '-'}</TableCell>
                      <TableCell>{formatDate(sub.starts_at)}</TableCell>
                      <TableCell>{formatDate(sub.expires_at)}</TableCell>
                      <TableCell>
                        {sub.payment
                          ? t(
                              `subscription.paymentMethod.${sub.payment.method ?? 'manual'}`,
                              sub.payment.method === 'balance' ? 'С баланса' : 'Вручную',
                            )
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={getSubscriptionStatusLabel(sub, t).label}
                          color={getSubscriptionStatusLabel(sub, t).color}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={cancelDialogOpen}
          onClose={handleCancelDialogClose}
          aria-labelledby="cancel-dialog-title"
          aria-describedby="cancel-dialog-description"
        >
          <DialogTitle id="cancel-dialog-title">{t('subscription.cancelDialogTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText id="cancel-dialog-description">
              {t('subscription.cancelConfirm')}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCancelDialogClose} color="inherit">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCancelConfirm} color="error" variant="contained" autoFocus>
              {t('subscription.cancel')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ProfileLayout>
  )
}
